import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Label } from "@/pages/admin/AdminLayout";
import { WatermarkImage, WatermarkPreview } from "@/pages/admin/WatermarkUpload";

const CORNERS = [["tr", "Superior direito"], ["tl", "Superior esquerdo"], ["br", "Inferior direito"], ["bl", "Inferior esquerdo"]];
const RANGES = [["watermark_opacity", "Opacidade", 5, 60, 0.22, "%"], ["watermark_scale", "Tamanho", 4, 30, 0.12, "% da largura"], ["watermark_margin", "Distância à margem", 0, 12, 0.04, "% da largura"]];
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export function SegmentWatermarks({ segments, general, data, onSaved }) {
  const [seg, setSeg] = useState(segments[0] || "");
  if (!segments.length) return null;
  return (
    <div className="mt-6 pt-5 border-t border-[#282520]" data-testid="segment-watermarks">
      <h4 className="text-base mb-1">Marca de água por segmento</h4>
      <p className="text-xs text-[#A39E93] mb-4">Diferencie o nível de luxo: cada segmento pode ter a sua imagem e afinações. Sem configuração própria usa a marca geral acima. Ao mudar o segmento de um relógio, as fotos são refeitas automaticamente. Se renomear um segmento na Classificação, configure de novo a sua marca.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {segments.map((x) => (
          <button key={x} type="button" onClick={() => setSeg(x)} className={`px-3 py-1.5 text-xs border ${seg === x ? "border-[#C8AE6E] text-[#E6D5AC]" : "border-[#282520] text-[#A39E93]"}`} data-testid={`segment-wm-tab-${slug(x)}`}>
            {x}{data[x]?.enabled ? " ●" : ""}
          </button>
        ))}
      </div>
      {seg && <SegmentEditor key={seg} seg={seg} general={general} conf={data[seg] || {}} onSaved={onSaved} />}
    </div>
  );
}

function SegmentEditor({ seg, general, conf, onSaved }) {
  const [enabled, setEnabled] = useState(!!conf.enabled);
  const [mp, setMp] = useState({ ...general, ...(conf.mp || {}) });
  const [custom, setCustom] = useState(!!conf.custom);
  const [ver, setVer] = useState(0);
  const [busy, setBusy] = useState(false);
  const t = slug(seg);
  const set = (k, v) => setMp((m) => ({ ...m, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/settings/watermark-segments/${encodeURIComponent(seg)}`, { enabled, media_processing: mp });
      toast.success(`Marca de água de "${seg}" guardada. Use "Reaplicar a todo o catálogo" para atualizar as fotos existentes.`);
      onSaved(seg, { enabled, custom, mp });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reapply = async () => {
    if (!window.confirm(`Reaplicar a marca de água guardada às fotos dos relógios "${seg}"? Guarde antes as alterações.`)) return;
    try { await api.post(`/admin/media/rewatermark-all?segment=${encodeURIComponent(seg)}`); toast.success("Atualização iniciada. O progresso aparece em \"Reaplicar a todo o catálogo\"."); } catch (e) { toast.error(errMsg(e)); }
  };
  const previewMp = enabled ? { ...mp, aspect_crop: general?.aspect_crop } : general;
  return (
    <div data-testid={`segment-wm-editor-${t}`}>
      <label className="flex items-center gap-3 cursor-pointer mb-4">
        <input type="checkbox" className="accent-[#C8AE6E]" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`segment-wm-enabled-${t}`} />
        <span className="text-sm text-[#F3EFE6]">Usar configuração própria para "{seg}"</span>
      </label>
      {enabled && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 items-start">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="accent-[#C8AE6E]" checked={!!mp.watermark_on} onChange={(e) => set("watermark_on", e.target.checked)} data-testid={`segment-wm-on-${t}`} />
              <span className="text-sm">Aplicar marca de água</span>
            </label>
            {RANGES.map(([k, l, lo, hi, d, u]) => (
              <label key={k}><Label>{l} ({Math.round((mp[k] ?? d) * 100)}{u})</Label>
                <input type="range" min={lo} max={hi} step="1" value={Math.round((mp[k] ?? d) * 100)} onChange={(e) => set(k, Number(e.target.value) / 100)} className="w-full accent-[#C8AE6E]" data-testid={`segment-wm-${k.replace("watermark_", "")}-${t}`} />
              </label>
            ))}
            <label><Label>Canto</Label>
              <select className="afield" value={mp.watermark_corner || "tr"} onChange={(e) => set("watermark_corner", e.target.value)} data-testid={`segment-wm-corner-${t}`}>
                {CORNERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <WatermarkImage segment={seg} custom={custom} onChange={(v) => { setCustom(v); setVer((x) => x + 1); onSaved(seg, { enabled: !!conf.enabled, custom: v, mp: conf.mp || {} }); }} />
        </div>
      )}
      <WatermarkPreview mp={previewMp} ver={ver} segment={enabled ? seg : ""} tid={`-seg-${t}`} />
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" className="btn-gold !py-2 text-xs" disabled={busy} onClick={save} data-testid={`segment-wm-save-${t}`}>{busy ? "A guardar…" : `Guardar marca de "${seg}"`}</button>
        <button type="button" className="btn-line !py-2 text-xs" onClick={reapply} data-testid={`segment-wm-reapply-${t}`}>Reaplicar às fotos de "{seg}"</button>
      </div>
    </div>
  );
}
